import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import isString from 'lodash/isString';
import { connect } from 'react-redux';

import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import CustomBox from '../common/CustomBox';
import ProviderAvatar from '../providers/ProviderAvatar';
import WizardStepper from './WizardStepper';
import PatientSchedulingServiceForm from './PatientSchedulingServiceForm';
import PatientSchedulingLocationForm from './PatientSchedulingLocationForm';
import PatientSchedulingTimeForm from './PatientSchedulingTimeForm';
import NewOrExistingPatient from './NewOrExistingPatient';
import { useGetSessionTypesQuery } from '../../redux/services/sessionTypeApi';
import {formatProviderName} from '../../helpers/providers';
import { 
  setDate, 
  setTime, 
  setSessionTypeId, 
  setProviderAvailabilityId,
  setLocationType,
  setScheduleManually,
  reset,
} from '../../redux/reducers/newAppointmentForm';

const steps = ({
  provider,
  formState,
  serviceCategories,
  sessionTypes,
  setField,
  saveDate,
  saveTime,
  saveSessionTypeId,
  saveProviderAvailabilityId,
  saveLocationType,
  saveScheduleManually,
  onScheduleManually,
  next,
  navigate,
}) => [
  {
    label: 'Account',
    Component: NewOrExistingPatient,
    onSubmit: (e, isNewPatient) => {
      if (isNewPatient) {
        next();
      } else {
        navigate('/login');
      }
    },
  },
  {
    label: 'Session Type',
    Component: PatientSchedulingServiceForm,
    provider,
    providerId: provider?.id,
    formState,
    allowedServiceCategories: serviceCategories,
    sessionTypes,
    onSubmit: (e, service) => {
      console.log('Selected service:', service);
      setField('serviceCategory')(service);
      const sessionType = sessionTypes.find(st => st.service_category === service);
      saveSessionTypeId(sessionType.id);
      next();
    },
  },
  {
    label: 'Location',
    Component: PatientSchedulingLocationForm,
    onSubmit: (e, location) => {
      console.log('Selected location type:', location);
      setField('location')(location);
      next();
    },
  },
  {
    label: 'Schedule',
    Component: props => (
      <Stack spacing={1} alignItems='center'>
        <Typography variant='h2'>Step 4: Schedule a Time</Typography>
        <Typography>What time works best for you?</Typography>
        <Container maxWidth='xs'>
          <PatientSchedulingTimeForm 
            includeLocation={true} 
            onScheduleManually={() => {
              saveScheduleManually(true);
              saveDate(null);
              saveTime(null);
              saveProviderAvailabilityId(null);
              saveLocationType(null);
              navigate(`/signup?alreadyScheduled=true&selectedProviderId=${provider?.id}&version=v2`);
            }}
            {...props} 
          />
        </Container>
      </Stack>
    ),
    onSubmit: (e, slot, location) => {
      const start = slot?.start_time;
      if (start) {
        const dateStr = moment(start).format('MM/DD/yyyy');
        const timeStr = moment(start).format('hh:mm a');
        saveDate(dateStr);
        saveTime(timeStr);
        saveProviderAvailabilityId(slot.availability_id);
        setField('start_time')(start);
      }
      if (isString(location)) {
        setField('location_type')(location);
        saveLocationType(location);
      }
      navigate(`/signup?alreadyScheduled=true&selectedProviderId=${provider?.id}&version=v2`);
    },
  },
];

const BookingFromPublicProviderProfileForm = ({
  provider,
  saveDate,
  saveTime,
  saveSessionTypeId,
  saveProviderAvailabilityId,
  saveLocationType,
  saveScheduleManually,
  reset,
  borderRadius,
  boxStyles = {minHeight: '90vh', borderRadius},
}) => {
  const [stepIndex, setStepIndex] = useState(0);
  const [formState, setFormState] = useState({
    serviceCategory: null,
    location: null,
    datetime: null,
    locationType: null,
  });
  const navigate = useNavigate();
  const sessionTypeQuery = useGetSessionTypesQuery({
    provider_id: provider?.id,
    session_type_version: '2.0',
  }, {skip: !provider?.id});
  useEffect(() => {
      reset();
    }, []);
  const _steps = steps({
    provider, 
    formState,
    serviceCategories: (sessionTypeQuery?.data || []).map(st => st.service_category) || [],
    sessionTypes: sessionTypeQuery?.data || [],
    saveDate,
    saveTime,
    saveSessionTypeId,
    saveProviderAvailabilityId,
    saveLocationType,
    saveScheduleManually,
    setField: (field) => value => setFormState({...formState, [field]: value}),
    next: () => setStepIndex(stepIndex + 1),
    navigate,
  });
  const stepConfig = _steps[stepIndex];
  const FormComponent = stepConfig.Component;
  const childProps = {
    ...stepConfig,
    stepNum: stepIndex,
    providerId: provider?.id,
    serviceCategory: formState.serviceCategory,
    lat: formState?.location?.lat,
    lon: formState?.location?.lon,
  };
  return (
    <Box style={boxStyles}>
      <WizardStepper
        currentStepIndex={stepIndex}
        steps={_steps} 
        onBack={stepIndex > 0 ? () => setStepIndex(stepIndex - 1) : null}
        borderRadius={
          boxStyles?.borderRadius 
          ? `${boxStyles.borderRadius} ${boxStyles.borderRadius} 0px 0px`
          : null
        }
      />
      <Box pr={2} pl={2}>
        <CustomBox mb={2}>
          <Stack p={1}>
            <Stack direction='row' alignItems='center' gap={1}>
              <ProviderAvatar provider={provider} />
              <Stack>
                <Typography variant='h3' align='center'>
                  Schedule with {formatProviderName(provider)}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </CustomBox>
        <FormComponent {...childProps} />
      </Box>
    </Box>
  );
};

const mapState = state => ({});
const mapDispatch = dispatch => ({
  saveDate: dateStr => dispatch(setDate(dateStr)),
  saveTime: timeStr => dispatch(setTime(timeStr)),
  saveSessionTypeId: sessionTypeId => dispatch(setSessionTypeId(sessionTypeId)),
  saveProviderAvailabilityId: providerAvailabilityId => 
    dispatch(setProviderAvailabilityId(providerAvailabilityId)),
  saveLocationType: locationType => dispatch(setLocationType(locationType)),
  saveScheduleManually: isManual => dispatch(setScheduleManually(isManual)),
  reset: () => dispatch(reset()),
});

export default connect(mapState, mapDispatch)(BookingFromPublicProviderProfileForm);